import cn, { clsx } from "clsx"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTable, usePagination, useRowSelect, useBlockLayout, useResizeColumns } from "react-table"
import { Skeleton, Box, Button, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react"
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons"
import { CSVLink } from "react-csv"

import { FiAlertCircle } from "react-icons/fi"
import { BeatTableProps } from "./types"

import { PageButton } from "./Pagination/PageButton"

import { BeatTableContainer, BeatTbody, BeatTd, BeatTh, BeatThead, BeatTr } from "./index"

import { IndeterminateCheckbox } from "./Checkbox"

import { TableFilter } from "./Filters"
import { useDebounce } from "use-debounce"
import { useQueryState } from "context/queryState"
import { useQuery } from "hooks/useQuery"
import { FaArrowDown, FaDownload, FaRetweet, FaSlidersH, FaWindowMaximize } from "react-icons/fa"
import ActionsHeader from "./ActionsHeader"
import styled from "styled-components"
import { Select } from "../Select"
import Heading from "components/Typography/Heading"
import { SortDirection, SortState } from "constants/table-filters"
import moment from "moment"
import BodyText from "components/Typography/BodyText"
import { BeatHeading } from "../BeatTypography"
import { makeArrayWithLength } from "../utils/makeArrayWithLength"
import { themeStyle } from "../utils/themeStyle"
import IconWhiteEye from "components/icons/Beat/IconWhiteEye"
import IconCircleNew from "components/icons/Beat/IconCircleNew"
import IconMenu from "components/icons/Beat/IconMenu"
import IconCaretDownGray from "components/icons/Beat/IconCaretDownGray"
import IconDesc from "components/icons/Beat/IconDesc"
import IconAsc from "components/icons/Beat/IconAsc"

const PAGINATION_OPTIONS = [
  { label: "10", value: "10" },
  { label: "20", value: "20" },
  { label: "30", value: "30" },
  { label: "40", value: "40" },
  { label: "50", value: "50" }
]

const Container = styled.div`
  &.expanded {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1000;
  }
`

const SelectWrapper = styled.div`
  width: 220px;
`

const LeftFooterContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;
  width: 50%;
  color: ${(props) => props.theme.palette.primary_dark};
`

const RightFooterContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
`

const BeatTableCustom = styled(BeatTableContainer)`
  width: 100%;
  table {
    display: inline-block;
    border-spacing: 0;
  }
`

const BeatTheadCustom = styled(BeatThead)`
  tr,
  th {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  background-color: ${({ theme }) => theme.palette.medium_white};
`

const BeatTfoot = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: "Inter";

  &.expanded {
    margin-top: auto;
  }
`

const PaginationButton = styled.button`
  width: 50px;
  height: 50px;
  outline: none;
  color: ${(props) => props.theme.palette.primary_dark};
  border: none;
  background: transparent;
  border-radius: 16px;
  font-size: 100%;
  font-family: "Inter";

  &:hover {
    ${themeStyle({ backgroundColor: "primary" })};
    width: 50px;
    height: 50px;
    color: ${({ theme }) => theme.palette.primary_one};
    border-radius: 16px;
    background-color: ${(props) => props.theme.palette.secondary_gray_light};
  }
`

const PaginatorView = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const BeatTableBodyWrapper = styled.div`
  overflow-x: auto;
  height: fit-content;
  width: 100%;
  padding-left: 20px;

  table {
    margin: 0;
    border-spacing: 0;
  }

  &.expanded {
    flex: 1;
  }
`

const BeatTrCustomHead = styled(BeatTr)`
  cursor: default;
  height: 42px;
  border-radius: 16px;
  border: 2px solid white;
`

const BeatTrCustom = styled(BeatTr)`
  height: 54px;
  border-radius: 16px;

  &.fullHeightSize {
    height: 58px;
  }

  &:hover {
    background-color: ${(props) => props.theme.palette.secondary_gray_light};

    td {
      background-color: ${(props) => props.theme.palette.secondary_gray_light};
    }
  }
`

const BeatThCustom = styled(BeatTh)`
  margin: 0;
  padding: 0.5rem;
  border: none;

  &.isResizable {
    ${themeStyle({ borderColor: "neutral.300", color: "neutral.400", stroke: "neutral.300" })};
    border-right: 1px solid;
  }

  ${""}
  position: relative;

  :last-child {
    border-right: 0;
  }

  :not(:last-child) {
    .resizer {
      display: inline-block;
      width: 18px;
      height: 18px;
      position: absolute;
      right: -1px;
      top: 1px;
      transform: translateX(50%);
      z-index: 1;
      ${""}
      touch-action:none;
    }
  }

  &.isCheckable {
    :first-of-type {
      width: 60px;
      border-right: none;

      .resizer {
        display: none;
      }
    }
  }

  &:hover {
    background-color: transparent;
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 8px;

    &.hideMenu {
      gap: 0;
    }
  }
`

const BeatTdCustom = styled(BeatTd)`
  margin: 0;
  padding: 0.5rem;
  border: none;
  position: relative;
  background-color: transparent;

  :last-child {
    border-right: 0;
  }

  &.isCheckable {
    :first-of-type {
      width: 60px;
      border-right: none;

      .resizer {
        display: none;
      }
    }
  }
`

const ActionHeader = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 3fr 1fr;
  padding: 20px;
  gap: 48px;
`

const ActionItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 48px;
`

const RightActionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 48px;
`

const BeatTableWrapper = styled.div`
  &.fullHeightSize {
    height: 92vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  box-shadow: 0px 1px 4px 0px #00000026;
  background-color: ${(props) => props.theme.palette.white};
  border-radius: 8px;
  margin-bottom: 8px;

  &.expanded {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    margin-bottom: 0px;
  }
`

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 64px;
  align-items: center;
  justify-content: center;

  color: #555555;

  svg {
    stroke: #555555;
    margin-bottom: 16px;
  }
`

const SortMenu = styled.div`
  cursor: pointer;
`

const IconArrowDown = styled.div`
  transform: rotate(180deg);
`

const IconArrowUp = styled.div`
  transform: rotate(0deg);
`

export const BeatTable = ({
  columnsjson,
  data,
  loading,
  totalItems = 0,
  hasPageSizeSelect = true,
  onClickRow = () => undefined,
  selectedItems = () => undefined,
  isCheckable = false,
  isResizable,
  cellWidth,
  cellMaxWidth,
  cellMinWidth,
  fullHeight,
  fullWidth,
  applyParamsToQuery = false,
  navigate,
  hasPagination = true,
  filters,
  action,
  actionSummary,
  cacheKey,
  getData,
  getDataSummary,
  setFilters,
  hideActions = true,
  onAddNew,
  defaultSortBy,
  disableCache = false,
  hideMenu = true
}: BeatTableProps) => {
  const queryParams = useQuery()
  const [, setPageField] = useState(1)
  const [formattedPageCount, setFormattedPageCount] = useState<number>(0)
  const [skip, setSkip] = useState<string>(queryParams.get("skip") ?? "0")
  const [take, setTake] = useState<string>(queryParams.get("take") ?? "10")
  const [openFilters, setOpenFilters] = useState<boolean>(false)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])
  const [columnsToHide, setColumnsToHide] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<SortState | undefined>(defaultSortBy)
  const [dataToDownload, setDataToDownload] = useState<string[][]>([])
  const [dataUpdatedAt, setDataUpdatedAt] = useState<number>(0)

  const csvRef = useRef<CSVLink>(null)
  const refreshRef = useRef<() => void>()
  const updatedAtRef = useRef<number>(0)

  const toggleColumn = useCallback(
    (column: string) => {
      if (columnsToHide.includes(column)) {
        setColumnsToHide(columnsToHide.filter((col) => col !== column))
      } else {
        setColumnsToHide([...columnsToHide, column])
      }
    },
    [columnsToHide]
  )

  const triggerExpand = useCallback(() => {
    setExpanded(!expanded)
  }, [expanded])

  const triggerColumns = useCallback(() => {
    if (columnsToHide.length > hiddenColumns.length) {
      setHiddenColumns(columnsToHide)
      setTableHiddenColumns(columnsToHide)
    } else {
      setTableHiddenColumns([])
      setHiddenColumns([])
    }
  }, [columnsToHide, hiddenColumns, setHiddenColumns])

  const toggleSort = useCallback((column: string, direction: SortDirection) => {
    setSortBy({ sortBy: column, sortDirection: direction })
  }, [])

  const downloadData = useCallback(() => {
    if (!data) return

    const columns = columnsjson

    const preparedData: string[][] = []

    const header: string[] = columnsjson
      .filter((col) => !hiddenColumns.includes(col.accessor))
      .map((col) => col.filter?.label || col.accessor)

    preparedData.push(header)

    for (let index = 0; index < data.length; index++) {
      const row = data[index]
      const rowValues: string[] = []

      for (let i = 0; i < columns.length; i++) {
        const col = columns[i]

        if (hiddenColumns.includes(col.accessor)) continue

        const value = row[col.accessor]

        rowValues.push(value ?? "")
      }

      preparedData.push(rowValues)
    }

    setDataToDownload(preparedData)
  }, [data, columnsjson, hiddenColumns])

  useEffect(() => {
    if (dataToDownload.length > 0) {
      csvRef.current?.link.click()
      setDataToDownload([])
    }
  }, [dataToDownload])

  const actionHeaderOptions = useMemo(() => {
    const hasSelectedColumnsUnhidden = columnsToHide.length - hiddenColumns.length

    const actions = [
      {
        icon: <FaSlidersH fontWeight="thin" />,
        title: "Filtros",
        onClick: () => {
          setOpenFilters(true)
        }
      },
      /*
      TODO: Validate if this option is necessary
      {
        icon: <FiLayout fontWeight='thin' />,
        title: 'Formatação',
      },*/
      {
        icon: <FaDownload />,
        title: "Download",
        onClick: () => downloadData()
      },
      {
        icon: expanded ? <FaArrowDown /> : <FaWindowMaximize />,
        title: expanded ? "Contrair" : "Expandir",
        onClick: () => triggerExpand()
      },
      {
        icon: <FaRetweet />,
        title: "Atualizar Tabela",
        onClick: () => refreshRef.current?.()
      },
      {
        icon: <IconWhiteEye />,
        title: hasSelectedColumnsUnhidden === 0 && hiddenColumns.length > 0 ? "Restaurar colunas" : "Ocultar colunas",
        disabled: hasSelectedColumnsUnhidden === 0 && hiddenColumns.length === 0,
        onClick: () => triggerColumns()
      }
    ]

    if (onAddNew) {
      actions.unshift({
        icon: <IconCircleNew />,
        title: "Novo",
        onClick: onAddNew
      })
    }

    return actions
  }, [onAddNew, expanded, triggerColumns, hiddenColumns, columnsToHide, downloadData])

  if (getData) {
    const { refetchAndBust, dataUpdatedAt } = useQueryState(
      [cacheKey, action, filters, skip, take, sortBy],
      async () => getData(skip, take, filters, sortBy),
      action ?? "",
      disableCache ? { cacheTime: 0 } : {}
    )

    refreshRef.current = refetchAndBust
    updatedAtRef.current = dataUpdatedAt
  }

  if (actionSummary && getDataSummary) {
    const debouncedSummaryQueryKey = useDebounce([actionSummary, filters], 600)

    useQueryState(debouncedSummaryQueryKey, async () => getDataSummary(filters), actionSummary)
  }

  const fetchData = useCallback(async (params: { skip: number; take: number }) => {
    return new Promise(() => {
      setSkip(String(params.skip))
      setTake(String(params.take))
    }) as Promise<void>
  }, [])

  const hasLoadedInitialState = useRef(false)

  const defaultColumnSize = useMemo(() => {
    const size = { width: fullWidth ? "100%" : "auto" }

    if (!isResizable) return size

    return Object.assign(size, {
      minWidth: cellMinWidth ?? 30,
      width: cellWidth ?? 150,
      maxWidth: cellMaxWidth ?? 500
    })
  }, [isResizable, cellMinWidth, cellWidth, cellMaxWidth, fullWidth])

  useEffect(() => {
    if (updatedAtRef.current !== dataUpdatedAt) {
      setDataUpdatedAt(dataUpdatedAt)
    }
  }, [updatedAtRef.current])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    resetResizing,
    page,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
    selectedFlatRows,
    setHiddenColumns: setTableHiddenColumns
  } = useTable(
    {
      columns: columnsjson,
      data: data ?? [],
      initialState: { pageIndex: Number(skip) / Number(take) ?? 0, pageSize: Number(take) ?? 10 },
      manualPagination: true,
      pageCount: formattedPageCount,
      defaultColumn: defaultColumnSize
    },
    isResizable ? useBlockLayout : usePagination,
    usePagination,
    useResizeColumns,
    useRowSelect,
    (hooks) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isCheckable &&
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            )
          },
          ...columns
        ])
    }
  )

  const dropdownPages = useMemo(() => {
    const pageArray = makeArrayWithLength(formattedPageCount + 1)
    const pageArrayFormatted = pageArray.filter((item) => item !== 0)

    return pageArrayFormatted
  }, [formattedPageCount])

  const Paginator = useMemo(() => {
    if (formattedPageCount < 7) {
      return dropdownPages
    }

    if (pageIndex < 3) {
      return [...dropdownPages.slice(0, 4), "...", formattedPageCount]
    }

    if (pageIndex < formattedPageCount - 2) {
      return [1, "...", ...dropdownPages.slice(pageIndex - 1, pageIndex + 2), "...", formattedPageCount]
    }

    if (pageIndex > formattedPageCount - 3) {
      return [1, "...", ...dropdownPages.slice(formattedPageCount - 3)]
    }

    return dropdownPages
  }, [formattedPageCount, pageIndex])

  const handlePageClick = (item: number | string | undefined) => {
    if (typeof item !== "number") return

    gotoPage(item - 1)
    setPageField(item)
  }

  const goNextPage = useCallback(() => {
    gotoPage(pageIndex + 1)
    setPageField(pageIndex + 2)
  }, [gotoPage, pageIndex])

  const goPreviousPage = useCallback(() => {
    gotoPage(pageIndex - 1)
    setPageField(pageIndex)
  }, [gotoPage, pageIndex])

  const handleSetFilters = useCallback(
    (state: any) => {
      setFilters?.(state)
      gotoPage(0)
      setPageField(1)
    },
    [setFilters, setSkip]
  )

  const updatedAtDate = useMemo(
    () =>
      updatedAtRef.current ? `Atualizado em ${moment(updatedAtRef.current).format("DD/MM/YY [às] HH:mm:ss")}` : "",
    [updatedAtRef.current]
  )

  useEffect(() => {
    if (typeof fetchData !== "function") return

    const skip = pageIndex * pageSize

    if (applyParamsToQuery) {
      // Updates route params to reflect pagination
      const { pathname } = window.location
      const search = `?skip=${skip}&take=${pageSize}`

      navigate?.(`${pathname}${search}`)
    }

    fetchData({ skip, take: pageSize })
  }, [fetchData, pageIndex, pageSize])

  useEffect(() => {
    selectedItems(selectedFlatRows ?? [])
  }, [selectedFlatRows])

  useEffect(() => {
    setFormattedPageCount(Math.ceil(totalItems / pageSize))
  }, [totalItems, pageSize])

  useEffect(() => {
    if (!hasLoadedInitialState.current) {
      setPageField((Number(skip) / Number(take) ?? 0) + 1)
      hasLoadedInitialState.current = true
    }
  }, [skip, take])

  const resultsLabel = useMemo(
    () =>
      `${totalItems ? pageSize * pageIndex + 1 : pageSize * pageIndex} - ${
        pageSize * pageIndex + pageSize >= totalItems ? totalItems : pageSize * pageIndex + pageSize
      } de ${totalItems}`,
    [pageIndex, pageSize, totalItems]
  )

  useEffect(() => {
    gotoPage(0)
    setPageField(1)
  }, [filters])

  return (
    <Container className={cn({ expanded })}>
      {!hideActions && (
        <CSVLink data={dataToDownload} filename="data.csv" style={{ display: "none" }} ref={csvRef} target="_blank" />
      )}
      {openFilters && (
        <TableFilter
          open={openFilters}
          onClose={() => {
            setOpenFilters(false)
          }}
          filtersFields={columnsjson}
          initialFilters={filters}
          setFilterCallback={handleSetFilters}
        />
      )}
      <BeatTableWrapper
        className={cn({
          fullHeightSize: fullHeight,
          expanded
        })}
      >
        {!hideActions && (
          <ActionHeader>
            <div />
            <ActionItems>
              {actionHeaderOptions.map((option) => (
                <ActionsHeader
                  disabled={option.disabled}
                  key={option.title}
                  icon={option.icon}
                  title={option.title}
                  onClick={option.onClick}
                />
              ))}
            </ActionItems>
            <RightActionHeaderContent>
              <BodyText size="medium" color="gray-medium">
                {updatedAtDate}
              </BodyText>
            </RightActionHeaderContent>
          </ActionHeader>
        )}
        {!data?.length && !loading && (
          <NotFoundContainer>
            <FiAlertCircle size={48} />
            <BeatHeading size="h4" color="dark-gray">
              Nenhum item encontrado
            </BeatHeading>
          </NotFoundContainer>
        )}

        {(!!data?.length || loading) && (
          <BeatTableBodyWrapper className={cn({ expanded })}>
            <BeatTableCustom key={pageIndex} {...getTableProps()} columnsjson={columnsjson} loading>
              <BeatTheadCustom>
                {headerGroups.map((headerGroup, index) => (
                  <BeatTrCustomHead {...headerGroup.getHeaderGroupProps()} key={index} borderRadius={16}>
                    {headerGroup.headers.map((column, indexColumn) => (
                      <BeatThCustom
                        {...column.getHeaderProps()}
                        key={indexColumn}
                        className={cn(
                          {
                            isCheckable,
                            isResizable
                          },
                          "table-header"
                        )}
                      >
                        <div className={clsx("header-content", { hideMenu })}>
                          {!hideMenu && (
                            <>
                              <Menu>
                                <MenuButton as="button">
                                  <SortMenu>
                                    <IconMenu />
                                  </SortMenu>
                                </MenuButton>
                                <MenuList>
                                  <MenuItem
                                    onClick={() => toggleSort(column.id, SortDirection.ASC)}
                                    icon={<IconDesc />}
                                  >
                                    <Heading variant="h6">Crescente</Heading>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => toggleSort(column.id, SortDirection.DESC)}
                                    icon={<IconAsc />}
                                  >
                                    <Heading variant="h6">Decrescente</Heading>
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                              <IndeterminateCheckbox
                                checked={columnsToHide.includes(column.id)}
                                onChange={() => toggleColumn(column.id)}
                              />
                            </>
                          )}
                          {column.render("Header")}
                          {sortBy?.sortBy === column.id && (
                            <div>
                              {sortBy?.sortDirection === SortDirection.ASC && (
                                <IconArrowUp>
                                  <IconCaretDownGray />
                                </IconArrowUp>
                              )}
                              {sortBy?.sortDirection === SortDirection.DESC && (
                                <IconArrowDown>
                                  <IconCaretDownGray />
                                </IconArrowDown>
                              )}
                            </div>
                          )}

                          {isResizable && (
                            <div
                              {...column.getResizerProps()}
                              className={`resizer${cn({
                                isResizing: column.isResizing
                              })}`}
                            />
                          )}
                        </div>
                      </BeatThCustom>
                    ))}
                  </BeatTrCustomHead>
                ))}
              </BeatTheadCustom>
              {!!data?.length && !loading && (
                <BeatTbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row)

                    return (
                      <BeatTrCustom
                        className={cn({
                          fullHeightSize: fullHeight
                        })}
                        {...row.getRowProps()}
                      >
                        {row.cells.map((cell, index) => (
                          <BeatTdCustom
                            {...cell.getCellProps()}
                            key={index}
                            onClick={() => onClickRow(row.values)}
                            className={cn({
                              isCheckable
                            })}
                          >
                            <Skeleton isLoaded={!loading}>
                              <Box minWidth="fit-content" wordBreak="break-word" whiteSpace="nowrap">
                                {cell.render("Cell")}
                              </Box>
                            </Skeleton>
                          </BeatTdCustom>
                        ))}
                      </BeatTrCustom>
                    )
                  })}
                </BeatTbody>
              )}
              {loading && (
                <>
                  {!!data?.length && (
                    <BeatTbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row)

                        return (
                          <BeatTr {...row.getRowProps()}>
                            {row.cells.map((cell, index) => (
                              <BeatTd {...cell.getCellProps()} key={index.toString()}>
                                <Skeleton>loading</Skeleton>
                              </BeatTd>
                            ))}
                          </BeatTr>
                        )
                      })}
                    </BeatTbody>
                  )}
                  {!data?.length && (
                    <BeatTbody {...getTableBodyProps()}>
                      {Array.from(Array(10).keys()).map(() => (
                        <BeatTr>
                          {headerGroups?.[0].headers.map((_, index) => (
                            <BeatTd key={index.toString()}>
                              <Skeleton>loading</Skeleton>
                            </BeatTd>
                          ))}
                        </BeatTr>
                      ))}
                    </BeatTbody>
                  )}
                </>
              )}
            </BeatTableCustom>
          </BeatTableBodyWrapper>
        )}
        {!!data?.length && hasPagination && (
          <BeatTfoot>
            <LeftFooterContainer>
              {!!hasPageSizeSelect && (
                <SelectWrapper>
                  <Select
                    allowEmpty={false}
                    setValue={(value) => setPageSize(Number(value))}
                    options={PAGINATION_OPTIONS}
                    value={pageSize.toString()}
                    label="Resultados por página"
                  />
                </SelectWrapper>
              )}
              <span>{resultsLabel}</span>
            </LeftFooterContainer>
            <RightFooterContainer>
              <PaginationButton onClick={goPreviousPage} disabled={!canPreviousPage}>
                <ChevronLeftIcon />
              </PaginationButton>
              <PaginatorView>
                {Paginator.map((item, index) => (
                  <PageButton onChange={handlePageClick} page={item} pageIndex={pageIndex + 1} key={index} />
                ))}
              </PaginatorView>
              <PaginationButton onClick={goNextPage} disabled={!canNextPage}>
                <ChevronRightIcon />
              </PaginationButton>
            </RightFooterContainer>
            {isResizable && (
              <Box ml={8}>
                <Button onClick={resetResizing}>Reset Resizing</Button>
              </Box>
            )}
          </BeatTfoot>
        )}
      </BeatTableWrapper>
    </Container>
  )
}
